import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import classnamesBind from "classnames/bind";
import styles from "./ExpandableText.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";

const classnames = classnamesBind.bind(styles);

export const textStyles = {
  PARAGRAPH: "PARAGRAPH",
  DISCLAIMER: "DISCLAIMER",
};

const getFirstWordsToNumber = (text, number) => {
  return text.split(" ").slice(0, number).join(" ");
};

const getCondensedText = ({ text, readMoreText }) => {
  return `${getFirstWordsToNumber(text, 20)}... ${readMoreText}`;
};

const getTextClass = (textStyle, reviewIsInModal) => {
  return {
    [textStyles.PARAGRAPH]: [
      styles.paragraphText,
      reviewIsInModal
        ? styles.paragraphTextModal
        : styles.paragraphTextRecentReviews,
    ],
    [textStyles.DISCLAIMER]: styles.disclaimerText,
  }[textStyle];
};

const ExpandableText = ({
  text,
  hasTitle,
  readMoreTranslationKey,
  testId,
  textStyle = textStyles.PARAGRAPH,
  onExpandCallback,
  reviewIsInModal,
}) => {
  const { getTranslation } = useRuntimeConstants();
  const textElement = useRef();
  const [expandable, setExpandable] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!textElement.current) return;

    const { scrollHeight, clientHeight } = textElement.current;

    if (reviewIsInModal) {
      const lineHeight = parseFloat(
        window.getComputedStyle(textElement.current).lineHeight
      );
      const actualLines = Math.floor(scrollHeight / lineHeight);
      const maxLines = 19;
      if (actualLines > maxLines) setExpandable(true);
    } else {
      if (scrollHeight > clientHeight) setExpandable(true);
    }
  }, [textElement, reviewIsInModal]);

  useEffect(() => {
    if (onExpandCallback) onExpandCallback();
  }, [expanded, onExpandCallback]);

  return (
    <>
      {!expandable && (
        <p
          ref={textElement}
          className={classnames(
            styles.text,
            getTextClass(textStyle, reviewIsInModal),
            {
              headerSpace: !hasTitle,
            }
          )}
        >
          {text}
        </p>
      )}
      {expandable && (
        <button
          ref={textElement}
          className={classnames(
            styles.text,
            styles.button,
            getTextClass(textStyle, reviewIsInModal),
            {
              expanded,
              headerSpace: !hasTitle,
            }
          )}
          onClick={() => {
            setExpanded(true);
          }}
          data-testid={testId}
          aria-label={
            !expanded
              ? getCondensedText({
                  text,
                  readMoreText: getTranslation(readMoreTranslationKey),
                })
              : undefined
          }
        >
          {text}
          {!expanded && (
            <span className={styles.showMoreContainer}>
              <span
                className={classnames(styles.fade, {
                  paragraphFade: textStyle === textStyles.PARAGRAPH,
                  disclaimerFade: textStyle === textStyles.DISCLAIMER,
                })}
              />
              <div className={styles.readMore}>
                {getTranslation(readMoreTranslationKey)}
              </div>
            </span>
          )}
        </button>
      )}
    </>
  );
};

ExpandableText.propTypes = {
  testId: PropTypes.string.isRequired,
  readMoreTranslationKey: PropTypes.string.isRequired,
  hasTitle: PropTypes.bool,
  text: PropTypes.string,
  textStyle: PropTypes.oneOf([textStyles.PARAGRAPH, textStyles.DISCLAIMER]),
  onExpandCallback: PropTypes.func,
  reviewIsInModal: PropTypes.bool,
};

export default ExpandableText;
